import React, { useEffect, useMemo, useState } from "react";
import Chart from "../../common/ApexChart";
import { useNavigate } from "react-router-dom";
import allApi from "../../../../api/allApi";
import Loader from "../../common/Loader";
import NmsChartCard from "../../common/NmsChartCard";
import NmsTableSearch, { searchRows, sortRows } from "../../common/NmsTableSearch";
import { useLocalization } from "../../localization/LocalizationContext";
import { useNetwork } from "../../common/networkContext";
import { getApexTheme, hasChartSeries, useDocumentTheme } from "../../common/nmsChartTheme";
import noSignal from "../../../../assets/images/no-signal.svg";
import dotSignal from "../../../../assets/images/dot-signal.svg";
import oneSignal from "../../../../assets/images/one-signal.svg";
import twoSignal from "../../../../assets/images/two-signal.svg";
import fullSignal from "../../../../assets/images/full-signal.svg";

const CHART_HEIGHT = 260;
const CLIENT_FIELDS = [
  "serialNo",
  "connDeviceType",
  "deviceName",
  "ssid",
  "connDeviceModel",
  "platform",
  "mac",
  "ipAddr",
  "rssi",
];
const DEVICE_FIELDS = [
  "serialNumber",
  "deviceName",
  "hardwareVersion",
  "firmwareVersion",
  "ipAddress",
  "macAddress",
  "created_at",
  "health",
];

const rssiIcon = (rssi) => {
  if (rssi >= 80) return fullSignal;
  if (rssi >= 60) return twoSignal;
  if (rssi >= 40) return oneSignal;
  if (rssi >= 20) return dotSignal;
  return noSignal;
};

function WirelessDashboard() {
  const navigate = useNavigate();
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const theme = useDocumentTheme();
  const networkId = selectedNetworkId?.id ?? 6001;

  const [clientSeries, setClientSeries] = useState([]);
  const [connectedClientSeries, setConnectedClientSeries] = useState([]);
  const [consumptionSeries, setConsumptionSeries] = useState([]);
  const [channelSeries, setChannelSeries] = useState([]);
  const [trafficSeries, setTrafficSeries] = useState([]);
  const [wirelessDataSeries, setWirelessDataSeries] = useState([]);
  const [devices, setDevices] = useState([]);
  const [clients, setClients] = useState([]);
  const [license, setLicense] = useState("");

  const [clientsDuration, setClientsDuration] = useState("24h");
  const [trafficDuration, setTrafficDuration] = useState("24h");
  const [wirelessDataDuration, setWirelessDataDuration] = useState("24h");
  const [connectedClientsDuration, setConnectedClientsDuration] =
    useState("24h");
  const [clientDataDuration, setClientDataDuration] = useState("24h");
  const [channelDuration, setChannelDuration] = useState("24h");

  const [devicesLoading, setDevicesLoading] = useState(true);
  const [clientsListLoading, setClientsListLoading] = useState(true);
  const [clientLoading, setClientLoading] = useState(true);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [connectedClientsLoading, setConnectedClientsLoading] = useState(true);
  const [wirelessDataLoading, setWirelessDataLoading] = useState(true);
  const [dataConsumptionLoading, setDataConsumptionLoading] = useState(true);
  const [channelLoading, setChannelLoading] = useState(true);
  const [refreshingClients, setRefreshingClients] = useState(false);
  const [refreshingDevices, setRefreshingDevices] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [deviceQuery, setDeviceQuery] = useState("");
  const [clientSort, setClientSort] = useState({ key: "", direction: "" });
  const [deviceSort, setDeviceSort] = useState({ key: "", direction: "" });

  useEffect(() => {
    setLicense(localStorage.getItem("license") || "");
  }, []);

  useEffect(() => {
    if (!networkId) return;
    getDeviceDetails(networkId);
    getClientList(networkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId]);

  useEffect(() => {
    if (networkId) getClientDetail(networkId, clientsDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, clientsDuration]);

  useEffect(() => {
    if (networkId) getTrafficBandwidthData(networkId, trafficDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, trafficDuration]);

  useEffect(() => {
    if (networkId) {
      getConnectedClientPerSSID(networkId, connectedClientsDuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, connectedClientsDuration]);

  useEffect(() => {
    if (networkId) getWirelessDataConsumption(networkId, wirelessDataDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, wirelessDataDuration]);

  useEffect(() => {
    if (networkId) getClientDataConsumption(networkId, clientDataDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, clientDataDuration]);

  useEffect(() => {
    if (networkId) getChannelUtilizationData(networkId, channelDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, channelDuration]);

  async function getDeviceDetails(id) {
    setDevicesLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/deviceList?networkId=${id}`
      );
      setDevices(
        Array.isArray(response?.response) ? response.response : []
      );
    } catch (error) {
      console.error("Error fetching device data:", error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  }

  const getClientList = async (id) => {
    setClientsListLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/clientList?networkId=${id}`
      );
      setClients(Array.isArray(response?.response) ? response.response : []);
    } catch (error) {
      setClients([]);
    } finally {
      setClientsListLoading(false);
    }
  };

  const getTrafficBandwidthData = async (id, duration) => {
    setTrafficLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/totalBandWidth?commandType=5&duration=${duration}&networkId=${id}`
      );
      if (response.status === "OK" && Array.isArray(response.response)) {
        setTrafficSeries(
          response.response.map((item) => ({
            name: item.serialNumber,
            data: (item.data || []).map((trafficItem) => ({
              x: new Date(trafficItem.interval1).getTime(),
              y: trafficItem.totalUtilization,
            })),
          }))
        );
      } else {
        setTrafficSeries([]);
      }
    } catch (error) {
      console.error("Error fetching traffic data:", error);
      setTrafficSeries([]);
    } finally {
      setTrafficLoading(false);
    }
  };

  const getChannelUtilizationData = async (id, duration) => {
    setChannelLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/totalChannelUtilization?commandType=5&duration=${duration}&networkId=${id}`
      );
      if (response.status === "OK" && Array.isArray(response.response)) {
        setChannelSeries(
          response.response.map((device) => ({
            name: device.serialNumber,
            data: (device.data || []).map((entry) => ({
              x: new Date(entry.interval1).getTime(),
              y: entry.totalUtilization,
            })),
          }))
        );
      } else {
        setChannelSeries([]);
      }
    } catch (error) {
      console.error("Error fetching channel utilization data:", error);
      setChannelSeries([]);
    } finally {
      setChannelLoading(false);
    }
  };

  const getWirelessDataConsumption = async (id, duration) => {
    setWirelessDataLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/wirelessDataConsumption?commandType=5&duration=${duration}&networkId=${id}`
      );
      const data = Array.isArray(response?.response) ? response.response : [];
      if (response.status !== "OK" || data.length === 0) {
        setWirelessDataSeries([]);
        return;
      }
      const uniqueTimestamps = Array.from(
        new Set(
          data.flatMap((device) =>
            (device.data || []).map((entry) =>
              new Date(entry.interval1).getTime()
            )
          )
        )
      ).sort((a, b) => a - b);
      setWirelessDataSeries(
        data.map((device) => ({
          name: device.serialNumber,
          data: uniqueTimestamps.map((timestamp) => {
            const entry = (device.data || []).find(
              (item) => new Date(item.interval1).getTime() === timestamp
            );
            const mb = (entry ? entry.totalUtilization : 0) / (1024 * 1024);
            return { x: timestamp, y: Number(mb.toFixed(2)) };
          }),
        }))
      );
    } catch (error) {
      console.error("Error fetching wireless data:", error);
      setWirelessDataSeries([]);
    } finally {
      setWirelessDataLoading(false);
    }
  };

  const getConnectedClientPerSSID = async (id, duration) => {
    setConnectedClientsLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/connectedClientsPerSsid?commandType=5&duration=${duration}&networkId=${id}`
      );
      if (response.status === "OK" && Array.isArray(response.response)) {
        setConnectedClientSeries(
          response.response.map((item) => ({
            name: item.ssidName,
            data: (item.data || []).map((client) => {
              const date = new Date(client.interval1);
              if (duration === "8h" || duration === "24h") {
                date.setMinutes(0, 0, 0);
              } else {
                date.setHours(0, 0, 0, 0);
              }
              return { x: date.getTime(), y: client.totalClientSum };
            }),
          }))
        );
      } else {
        setConnectedClientSeries([]);
      }
    } catch (error) {
      console.error("Error fetching connected client data:", error);
      setConnectedClientSeries([]);
    } finally {
      setConnectedClientsLoading(false);
    }
  };

  const getClientDetail = async (id, duration) => {
    setClientLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/totalClients?commandType=5&duration=${duration}&networkId=${id}`
      );
      if (response.status !== "OK" || !Array.isArray(response.response)) {
        setClientSeries([]);
        return;
      }
      const seriesData = {};
      const isAggregatedByDay = duration === "7d" || duration === "30d";
      response.response.forEach((item) => {
        (item.data || []).forEach((client) => {
          let timestamp = new Date(client.interval1).getTime();
          if (isAggregatedByDay) {
            const date = new Date(timestamp);
            date.setHours(0, 0, 0, 0);
            timestamp = date.getTime();
          }
          if (!seriesData[item.serialNumber]) {
            seriesData[item.serialNumber] = {
              name: item.serialNumber,
              data: [],
            };
          }
          const existingPoint = seriesData[item.serialNumber].data.find(
            (point) => point.x === timestamp
          );
          if (existingPoint) {
            existingPoint.y += client.totalClients || 0;
          } else {
            seriesData[item.serialNumber].data.push({
              x: timestamp,
              y: client.totalClients || 0,
            });
          }
        });
      });
      setClientSeries(Object.values(seriesData));
    } catch (error) {
      console.error("Error fetching client detail data:", error);
      setClientSeries([]);
    } finally {
      setClientLoading(false);
    }
  };

  const getClientDataConsumption = async (id, duration) => {
    setDataConsumptionLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/clientDataConsumption?commandType=5&duration=${duration}&networkId=${id}`
      );
      const apiResponse = response?.response || [];
      if (!apiResponse.length) {
        setConsumptionSeries([]);
        return;
      }

      let fullRange = [];
      if (duration === "7d" || duration === "30d") {
        const days = duration === "7d" ? 7 : 30;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        fullRange = Array.from({ length: days }, (_, i) => {
          const d = new Date(today);
          d.setDate(today.getDate() - (days - 1 - i));
          return d;
        });
      } else {
        const hours = duration === "8h" ? 8 : 24;
        const now = new Date();
        now.setMinutes(0, 0, 0);
        fullRange = Array.from({ length: hours }, (_, i) => {
          const d = new Date(now);
          d.setHours(now.getHours() - (hours - 1 - i));
          return d;
        });
      }

      setConsumptionSeries(
        apiResponse.map((client) => {
          const consumptionMap = {};
          (client.data || []).forEach((entry) => {
            if (!entry.interval) return;
            const normalizedKey =
              duration === "7d" || duration === "30d"
                ? entry.interval.split(" ")[0]
                : entry.interval.replace(" ", "T").slice(0, 13);
            consumptionMap[normalizedKey] = Number(entry.totalConsumption) || 0;
          });
          return {
            name: `SN: ${client.serialNumber || "Unknown"}`,
            data: fullRange.map((date) => {
              const key =
                duration === "7d" || duration === "30d"
                  ? date.toISOString().split("T")[0]
                  : date.toISOString().slice(0, 13);
              return { x: date.getTime(), y: consumptionMap[key] ?? 0 };
            }),
          };
        })
      );
    } catch (error) {
      console.error("Error fetching client data:", error);
      setConsumptionSeries([]);
    } finally {
      setDataConsumptionLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes == null || Number.isNaN(bytes)) return "0 B";
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
  };

  const lineOptions = (duration, yTitle, yFormatter, tooltipY) =>
    getApexTheme({ duration, yTitle, yFormatter, tooltipY });

  const visibleClients = useMemo(
    () =>
      sortRows(
        searchRows(clients, clientQuery, CLIENT_FIELDS),
        clientSort.key,
        clientSort.direction
      ),
    [clients, clientQuery, clientSort]
  );

  const visibleDevices = useMemo(
    () =>
      sortRows(
        searchRows(devices, deviceQuery, DEVICE_FIELDS),
        deviceSort.key,
        deviceSort.direction
      ),
    [devices, deviceQuery, deviceSort]
  );

  const sortHeader = (config, setConfig, field, label) => (
    <th
      className={`inc-th-sort${config.key === field ? " is-active" : ""}`}
      onClick={() =>
        setConfig((prev) => ({
          key: field,
          direction:
            prev.key === field && prev.direction === "asc" ? "desc" : "asc",
        }))
      }
    >
      {label}
      <i className="fa fa-sort" aria-hidden="true" />
    </th>
  );

  const showPremium = license !== "Standard";
  const showClientData = license !== "Standard" && license !== "BUSINESS";

  return (
    <div className="nms-dash">
      <div className="nms-dash-toolbar">
        <button
          className="text-btn primary-btn"
          onClick={() => navigate("/wifi-config")}
        >
          {localizationStrings?.wifiConfiguration}
        </button>
      </div>

      <div className="two-column">
        <NmsChartCard
          title={localizationStrings?.clients}
          onExpand={() => navigate("/client-expandation")}
          duration={clientsDuration}
          onDurationChange={setClientsDuration}
          localizationStrings={localizationStrings}
          loading={clientLoading}
          hasData={hasChartSeries(clientSeries)}
        >
          <Chart
            key={`clients-${theme}-${clientsDuration}`}
            options={lineOptions(
              clientsDuration,
              "Number of Clients",
              (val) => (val == null ? "0" : Number(val).toFixed(0))
            )}
            series={clientSeries}
            type="line"
            height={CHART_HEIGHT}
          />
        </NmsChartCard>

        {showPremium && (
          <NmsChartCard
            title={localizationStrings?.trafficBandwidth}
            onExpand={() => navigate("/trafficBandwidth-expandation")}
            duration={trafficDuration}
            onDurationChange={setTrafficDuration}
            localizationStrings={localizationStrings}
            loading={trafficLoading}
            hasData={hasChartSeries(trafficSeries)}
          >
            <Chart
              key={`traffic-${theme}-${trafficDuration}`}
              options={lineOptions(
                trafficDuration,
                "Traffic (KBPS)",
                (val) => Math.round(val || 0)
              )}
              series={trafficSeries}
              type="line"
              height={CHART_HEIGHT}
            />
          </NmsChartCard>
        )}

        {showPremium && (
          <NmsChartCard
            title={localizationStrings?.connectedClientsPerSSID}
            onExpand={() => navigate("/ConnectedClients-expandation")}
            duration={connectedClientsDuration}
            onDurationChange={setConnectedClientsDuration}
            localizationStrings={localizationStrings}
            loading={connectedClientsLoading}
            hasData={hasChartSeries(connectedClientSeries)}
          >
            <Chart
              key={`ssid-${theme}-${connectedClientsDuration}`}
              options={lineOptions(
                connectedClientsDuration,
                "Client Count",
                (val) => Number(val || 0).toFixed(0)
              )}
              series={connectedClientSeries}
              type="line"
              height={CHART_HEIGHT}
            />
          </NmsChartCard>
        )}

        <NmsChartCard
          title={localizationStrings?.wirelessDataConsumption}
          onExpand={() => navigate("/wirelessDataGraph-expandation")}
          duration={wirelessDataDuration}
          onDurationChange={setWirelessDataDuration}
          localizationStrings={localizationStrings}
          loading={wirelessDataLoading}
          hasData={hasChartSeries(wirelessDataSeries)}
        >
          <Chart
            key={`wdata-${theme}-${wirelessDataDuration}`}
            options={{
              ...getApexTheme({
                duration: wirelessDataDuration,
                type: "bar",
                yTitle: "Total Utilization (MB)",
                yFormatter: (val) => `${Number(val || 0).toFixed(2)} MB`,
                tooltipY: (val) => formatBytes((val || 0) * 1024 * 1024),
              }),
              plotOptions: { bar: { horizontal: false, columnWidth: "40%" } },
            }}
            series={wirelessDataSeries}
            type="bar"
            height={CHART_HEIGHT}
          />
        </NmsChartCard>

        {showClientData && (
          <NmsChartCard
            title={localizationStrings?.clientDataConsumption}
            onExpand={() => navigate("/ClientData-expandation")}
            duration={clientDataDuration}
            onDurationChange={setClientDataDuration}
            localizationStrings={localizationStrings}
            loading={dataConsumptionLoading}
            hasData={hasChartSeries(consumptionSeries)}
          >
            <Chart
              key={`cdata-${theme}-${clientDataDuration}`}
              options={lineOptions(
                clientDataDuration,
                "Data Consumption (bytes)",
                (val) => (val == null ? "0" : Number(val).toFixed(0)),
                (val) => formatBytes(val)
              )}
              series={consumptionSeries}
              type="line"
              height={CHART_HEIGHT}
            />
          </NmsChartCard>
        )}

        <NmsChartCard
          title={localizationStrings?.channelUtilization}
          onExpand={() => navigate("/ChannelUtilization-expandation")}
          duration={channelDuration}
          onDurationChange={setChannelDuration}
          localizationStrings={localizationStrings}
          loading={channelLoading}
          hasData={hasChartSeries(channelSeries)}
        >
          <Chart
            key={`channel-${theme}-${channelDuration}`}
            options={lineOptions(
              channelDuration,
              "Channel Utilization (%)",
              (val) => (val == null ? "0" : Math.round(val))
            )}
            series={channelSeries}
            type="line"
            height={CHART_HEIGHT}
          />
        </NmsChartCard>
      </div>

      <div className="inc-card">
        <div className="inc-card-title">
          <h3>
            {localizationStrings?.clients} ({visibleClients.length})
          </h3>
          <div className="inc-card-button">
            <NmsTableSearch
              value={clientQuery}
              onChange={setClientQuery}
              placeholder={localizationStrings?.search || "Search clients"}
            />
            <button
              className="icon-btn"
              title="Refresh"
              onClick={async () => {
                setRefreshingClients(true);
                await getClientList(networkId);
                setRefreshingClients(false);
              }}
            >
              <i
                className={`fa ${
                  refreshingClients ? "fa-spinner fa-spin" : "fa-refresh"
                }`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
        <div className="inc-card-body">
          {clientsListLoading ? (
            <Loader />
          ) : (
            <div className="inc-card-table">
              <table className="m-head">
                <thead>
                  <tr>
                    {sortHeader(
                      clientSort,
                      setClientSort,
                      "serialNo",
                      localizationStrings?.clients
                    )}
                    {sortHeader(
                      clientSort,
                      setClientSort,
                      "connDeviceType",
                      localizationStrings?.connection
                    )}
                    {sortHeader(
                      clientSort,
                      setClientSort,
                      "deviceName",
                      localizationStrings?.deviceName
                    )}
                    {sortHeader(clientSort, setClientSort, "ssid", "SSID")}
                    {sortHeader(
                      clientSort,
                      setClientSort,
                      "connDeviceModel",
                      localizationStrings?.model
                    )}
                    {sortHeader(clientSort, setClientSort, "platform", "OS")}
                    {sortHeader(
                      clientSort,
                      setClientSort,
                      "mac",
                      localizationStrings?.macAddress
                    )}
                    {sortHeader(
                      clientSort,
                      setClientSort,
                      "ipAddr",
                      localizationStrings?.ipAddress
                    )}
                    {sortHeader(clientSort, setClientSort, "rssi", "RSSI")}
                  </tr>
                </thead>
                <tbody>
                  {visibleClients.length ? (
                    visibleClients.map((client, index) => (
                      <tr key={client.mac || index}>
                        <td>{client.serialNo || "N/A"}</td>
                        <td>{client.connDeviceType || "N/A"}</td>
                        <td>{client.deviceName || "N/A"}</td>
                        <td>{client.ssid || "N/A"}</td>
                        <td>{client.connDeviceModel || "N/A"}</td>
                        <td>{client.platform || "N/A"}</td>
                        <td>{client.mac || "N/A"}</td>
                        <td>{client.ipAddr || "N/A"}</td>
                        <td className="rssi-icon">
                          <img src={rssiIcon(client.rssi)} alt="" />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <div className="nms-empty">
                          {localizationStrings?.noData}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="inc-card">
        <div className="inc-card-title">
          <h3>
            {localizationStrings?.devices} ({visibleDevices.length})
          </h3>
          <div className="inc-card-button">
            <NmsTableSearch
              value={deviceQuery}
              onChange={setDeviceQuery}
              placeholder={localizationStrings?.search || "Search devices"}
            />
            <button
              className="icon-btn"
              title="Refresh"
              onClick={async () => {
                setRefreshingDevices(true);
                await getDeviceDetails(networkId);
                setRefreshingDevices(false);
              }}
            >
              <i
                className={`fa ${
                  refreshingDevices ? "fa-spinner fa-spin" : "fa-refresh"
                }`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
        <div className="inc-card-body">
          {devicesLoading ? (
            <Loader />
          ) : (
            <div className="inc-card-table">
              <table className="m-head">
                <thead>
                  <tr>
                    {sortHeader(
                      deviceSort,
                      setDeviceSort,
                      "serialNumber",
                      localizationStrings?.serialNumber
                    )}
                    {sortHeader(
                      deviceSort,
                      setDeviceSort,
                      "deviceName",
                      localizationStrings?.deviceName
                    )}
                    {sortHeader(
                      deviceSort,
                      setDeviceSort,
                      "hardwareVersion",
                      localizationStrings?.hardwareVersion
                    )}
                    {sortHeader(
                      deviceSort,
                      setDeviceSort,
                      "firmwareVersion",
                      localizationStrings?.firmwareVersion
                    )}
                    {sortHeader(
                      deviceSort,
                      setDeviceSort,
                      "ipAddress",
                      localizationStrings?.ipAddress
                    )}
                    {sortHeader(
                      deviceSort,
                      setDeviceSort,
                      "macAddress",
                      localizationStrings?.macAddress
                    )}
                    {sortHeader(
                      deviceSort,
                      setDeviceSort,
                      "created_at",
                      localizationStrings?.upTime
                    )}
                    {sortHeader(
                      deviceSort,
                      setDeviceSort,
                      "health",
                      localizationStrings?.status
                    )}
                  </tr>
                </thead>
                <tbody>
                  {visibleDevices.length ? (
                    visibleDevices.map((device, index) => (
                      <tr key={device.serialNumber || index}>
                        <td>{device.serialNumber || "N/A"}</td>
                        <td>{device.deviceName || "N/A"}</td>
                        <td>{device.hardwareVersion || "N/A"}</td>
                        <td>{device.firmwareVersion || "N/A"}</td>
                        <td>{device.ipAddress || "N/A"}</td>
                        <td>{device.macAddress || "N/A"}</td>
                        <td>{device.created_at || "N/A"}</td>
                        <td>
                          <span
                            className={`d-status ${
                              String(device?.health || "").toLowerCase() ===
                              "offline"
                                ? "offline"
                                : "online"
                            }`}
                          >
                            {device.health || "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="nms-empty">
                          {localizationStrings?.noData}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WirelessDashboard;
